import {
  categoryState,
  downloadCategories,
  fetchCategories,
  modifyCategory
} from '../../redux_store/slices/categorySlice';
import { t } from 'i18next';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import * as Button from '../../components/buttons/BakeasyButtons';
import { BakeasyChipEdit } from '../../components/common/BakeasyChip';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import BakeasySwitch from '../../components/common/BakeasySwitch';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import { BakeasySelect } from '../../components/dropdowns/BakeasySelect';
import BakeasyHeaderLabel from '../../components/lables/BakeasyHeaderLabel';
import BakeasyTable from '../../components/tables/BakeasyTable';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import { authActions } from '../../redux_store/slices/authSlice';
import { clearProductState } from '../../redux_store/slices/productsSlice';
import { clearSubCategoryState } from '../../redux_store/slices/subCategorySlice';
import { STATUS_ALL_ARRAY1, UNAUTHORIZED } from '../../constants/Constants';
import { AppContext } from '../../context/AppContext';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import '../../styles/styles.css';
import { categoriesTableHeaders } from '../../constants/TableHeaders';
import { preparePayload } from '../../utils/commonUtils';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const Categories: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();
  const tableRef = React.useRef<any>();
  let myContext = React.useContext(AppContext);

  const [count, setCount] = React.useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [categoryList, setCategoryList] = useState<any[]>([]);

  let { error, value, rowsPerPage, pageNo, filter } = useAppSelector(categoryState);

  interface CategoryData {
    categoryName?: any;
    _id?: any;
    categoryId: string;
    name: string;
    description?: string;
    status: any;
  }

  function openAddEditDialog(isEdit: any, data: CategoryData) {
    return myContext.setDialog({
      open: true,
      title: isEdit ? 'EDIT_CATEGORY' : 'ADD_CATEGORY',
      dialogContent: {
        pageName: 'AddEditCategory',
        params: { isEdit, ...data },
        onBack: async () => {
          await refreshPageData(pageNo - 1, search, filter);
        }
      }
    });
  }

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((category: any) => {
        return {
          _id: category?._id,
          categoryCheck: false,
          categoryName: category?.categoryName,
          description: category?.description,
          onCategoryNameClick: () => {
            dispatch(clearSubCategoryState());
            dispatch(clearProductState());
            navigate('/subCategories', {
              state: { name: category?.categoryName, categoryId: category?._id }
            });
          },
          actions: (
            <BakeasyChipEdit
              key={category?._id}
              onClick={(event: any) => {
                event.stopPropagation();
                openAddEditDialog(true, {
                  categoryId: category?._id,
                  name: category?.categoryName,
                  description: category?.description,
                  status: category?.status
                });
              }}
            />
          ),
          status: (
            <BakeasySwitch
              value={category?.status === 'Active' ? true : false}
              onChange={(event: any) => {
                event.stopPropagation();
                category?.status === 'Active'
                  ? openDeActivateSupplierConfirmDialog(category)
                  : openReActivateSupplierConfirmDialog(category);
              }}
            />
          )
        };
      });

      setCategoryList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  function openReActivateSupplierConfirmDialog(category: CategoryData) {
    myContext.setConfirmDialog({
      type: 'reActivate',
      open: true,
      title: t('REACTIVATE_CATEGORY'),
      message: t('REACTIVATE_CATEGORY_MESSAGE'),
      onNoClick: () => {},
      onYesClick: (event: any, param: string | undefined | boolean) => {
        updateCategoryStatus(category, 'Active');
      }
    });
  }

  function openDeActivateSupplierConfirmDialog(category: CategoryData) {
    myContext.setConfirmDialog({
      type: 'deActivate',
      open: true,
      title: t('DEACTIVATE_CATEGORY'),
      message: t('DEACTIVATE_CATEGORY_MESSAGE'),
      onNoClick: () => {},
      onYesClick: (event: any, param: string | undefined | boolean) => {
        updateCategoryStatus(category, 'Inactive');
      }
    });
  }

  const updateCategoryStatus = async (category: CategoryData, status: string) => {
    try {
      const payload = {
        id: category._id,
        categoryName: category.categoryName,
        description: category.description,
        status
      };
      await dispatch(modifyCategory({ ...payload }));
      await refreshPageData(pageNo - 1, search, filter);
    } catch (error) {
      //dispatch(showErrorSnackbar(error));
    }
  };

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  const refreshPageData = async (newPage: number, search?: string, filter?: {} | undefined) => {
    let payload = await preparePayload(
      { limit: rowsPerPage, page: newPage + 1, sortBy: 'createdDate', sortDirection: -1 },
      filter,
      search,
      newPage
    );
    await dispatch(fetchCategories(payload));
    // await fetchData(fetchCategories, payload);

  };

  function CategoriesUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={'CATEGORIES'} />

          <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (event?.target?.value?.length === 0 || event?.target?.value?.length > 2) {
                    await refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0 ? undefined : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  await refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={async (event: React.KeyboardEvent<HTMLInputElement>) => {
                  await refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <BakeasySelect
                disabled={false}
                label={t('STATUS')}
                size="small"
                fullWidth={true}
                statusOptions={STATUS_ALL_ARRAY1}
                onChange={async (e: any) => {
                  let payload: any = { status: e?.target?.value === 'All' ? '' : e?.target?.value };
                  await refreshPageData(pageNo - 1, search, payload);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <div className="tableOptions">
                <Button.Print
                  onClick={function(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(downloadCategories({ ...filter, downloadType: 'pdf' }));
                  }}
                />
                <Button.Export
                  onClick={function(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(downloadCategories({ ...filter, downloadType: 'csv' }));
                  }}
                />
                <Button.Add
                  onClick={function(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    openAddEditDialog(false, { categoryId: '', name: '', status: '' });
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <Grid>
            {categoryList?.length > 0 ? (
              <BakeasyTable
                ref={tableRef}
                pageNo={pageNo - 1}
                search={search}
                headers={categoriesTableHeaders}
                tableData={categoryList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return CategoriesUI();
};
